import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ExternalLink } from "lucide-react";

// ==========================================
// MAIN RENDER FUNCTION
// ==========================================
const renderMessageContent = (content, meta) => {
  
  if (!content) return null;

  // Priority 1: Use query_results from meta if available
  if (meta?.query_results && Array.isArray(meta.query_results) && meta.query_results.length > 0) {
    return <MessageWithTable content={content} meta={meta} />;
  }

  // Priority 2: Regular markdown content
  return (
    <ReactMarkdown 
      remarkPlugins={[remarkGfm]}
      components={getMarkdownComponents()}
    >
      {content}
    </ReactMarkdown>
  );
};

// ==========================================
// MESSAGE WITH TABLE (Using query_results)
// ==========================================
const MessageWithTable = ({ content, meta }) => {
  console.log('MessageWithTable - meta:', meta);
  console.log('MessageWithTable - query_results:', meta.query_results);
  console.log('MessageWithTable - row_count:', meta.row_count);
  
  // Remove code blocks completely from content
  const contentWithoutCodeBlock = content.replace(/```[\s\S]*?```/g, '');
  
  // Split by double newlines to separate paragraphs
  const paragraphs = contentWithoutCodeBlock
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);

  console.log('Paragraphs:', paragraphs);
  console.log('Table data length:', meta.query_results?.length);

  // Convert query results to Bootstrap table
  return (
    <div>
      {/* Render paragraphs before/after table */}
      {paragraphs.map((paragraph, index) => (
        <ReactMarkdown 
          key={`para-${index}`}
          remarkPlugins={[remarkGfm]}
          components={getMarkdownComponents()}
        >
          {paragraph}
        </ReactMarkdown>
      ))}

      {/* Bootstrap Table from query_results */}
      {console.log('About to render BootstrapTable')}
      <BootstrapTable data={meta.query_results} rowCount={meta.row_count} />
    </div>
  );
};

// ==========================================
// BOOTSTRAP TABLE COMPONENT (Pure React)
// ==========================================
const BootstrapTable = ({ data, rowCount }) => {
  console.log('BootstrapTable component - data:', data);
  console.log('BootstrapTable component - rowCount:', rowCount);
  
  if (!data || data.length === 0) {
    console.log('No data - returning null');
    return null;
  }

  // Get columns from data
  const hiddenKeys = ["id", "meta", "isdeleted", "updateFlag"];
  const columns = Array.from(new Set(data.flatMap(Object.keys))).filter(
    (key) => !hiddenKeys.includes(key)
  );

  console.log('Columns:', columns);
  console.log('Data rows:', data.length);

  return (
    <div className="table-responsive my-3">
      <table className="table table-striped table-bordered table-hover table-sm">
        <thead>
          <tr>
            {columns.map((col) => (
              <th 
                key={col}
                style={{
                  backgroundColor: '#e8f5f1',
                  color: '#099276',
                  fontWeight: 600,
                  padding: '12px',
                  borderBottom: '2px solid #099276',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatColumnName(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((col) => (
                <td 
                  key={col}
                  style={{
                    padding: '10px 12px',
                    verticalAlign: 'middle',
                  }}
                >
                  {formatCellValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      
      {/* Show row count if there are more results */}
      {rowCount && rowCount > data.length && (
        <div className="text-muted small">
          <em>Showing {data.length} of {rowCount} results</em>
        </div>
      )}
    </div>
  );
};

// ==========================================
// REGULAR MARKDOWN COMPONENTS
// ==========================================
const getMarkdownComponents = () => ({
  // Prevent code blocks from rendering
  code: ({ node, inline, className, children, ...props }) => {
    // If it's a code block (not inline), don't render it
    if (!inline) {
      return null;
    }
    // Render inline code normally
    return (
      <code 
        className="bg-light text-danger px-1 rounded" 
        style={{ fontSize: '0.9em' }}
        {...props}
      >
        {children}
      </code>
    );
  },

  // Pre blocks (also used for code blocks)
  pre: ({ node, ...props }) => null,

  // Headings
  h1: ({ node, ...props }) => (
    <h1 className="h5 text-success fw-semibold mt-3 mb-2" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="h6 text-success fw-semibold mt-3 mb-2" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="h6 fw-semibold mt-2 mb-1" {...props} />
  ),

  // Paragraphs
  p: ({ node, ...props }) => (
    <p className="mb-2" style={{ lineHeight: '1.6' }} {...props} />
  ),

  // Lists
  ul: ({ node, ...props }) => (
    <ul className="mb-2" style={{ paddingLeft: '1.5rem' }} {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="mb-2" style={{ paddingLeft: '1.5rem' }} {...props} />
  ),
  li: ({ node, ...props }) => (
    <li className="mb-1" {...props} />
  ),

  // Links
  a: ({ node, ...props }) => (
    <a 
      className="text-success text-decoration-none"
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontWeight: 500 }}
      {...props}
    >
      {props.children}
      <ExternalLink size={12} className="ms-1" style={{ display: 'inline' }} />
    </a>
  ),

  // Blockquotes
  blockquote: ({ node, ...props }) => (
    <blockquote 
      className="border-start border-success border-3 ps-3 text-muted my-2"
      {...props} 
    />
  ),

  // Strong/Bold
  strong: ({ node, ...props }) => (
    <strong className="fw-semibold" {...props} />
  ),

  // Emphasis/Italic
  em: ({ node, ...props }) => (
    <em className="fst-italic text-muted" {...props} />
  ),
});

// ==========================================
// HELPER FUNCTIONS
// ==========================================
const formatColumnName = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "None") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "✅ Yes" : "❌ No";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  if (typeof value === "string" && value.length > 50) {
    return value.slice(0, 47) + "...";
  }
  return String(value);
};

export default renderMessageContent;